import { handleActions } from 'redux-actions';
import _isBoolean from 'lodash/isBoolean';
import _get from 'lodash/get';
import { sendViewerSettings } from '../communication/actions';
import {
  startCall,
  acceptCall,
  rejectCall,
  cancelCall,
  endCall,
  toggleMic,
  toggleCamera,
  toggleScreenSharingSuccess,
  changeCameraFacingMode,
  toggleCameraFacingMode,
  startCallByRemote,
  acceptCallByRemote,
  rejectCallByRemote,
  cancelCallByRemote,
  endCallByRemote,
  toggleRemoteMic,
  toggleRemoteCamera,
  toggleRemoteScreenSharing,
  changeRemoteCameraFacingMode,
  setLocalStream,
  setRemoteStream,
  resetCall,
  resetRemoteCall,
  updateCallId,
  newCustomerCall,
  toggleChatVisibility,
  initialized,
  setStatus,
} from './actions';

const initRemoteState = {
  user: null,
  stream: null,
  streamVersion: 0, // to apply visible changes to the redux store if stream does not change it's instance
  isCalling: false,
  isAccepted: false,
  isRejected: false,
  isCancelled: false,
  isEnded: false,
  isMicrophoneMuted: false,
  isCameraEnabled: true,
  isScreenSharingEnabled: false,
  cameraFacingMode: 'user',
  error: null,
  uuid: null,
  direction: null, // outgoing / incoming
};

const initLocalState = {
  stream: null,
  streamVersion: 0, // to apply visible changes to the redux store if stream does not change it's instance
  isCalling: false,
  isAccepted: false,
  isRejected: false,
  isCancelled: false,
  isEnded: false,
  isMicrophoneMuted: false,
  isCameraEnabled: true,
  isScreenSharingEnabled: false,
  cameraFacingMode: 'user',
  error: null,
};

const initialState = {
  local: null,
  remotes: [],
  type: 'video',
  isChatVisible: false,
  status: { source: 'presenter', info: 'disconnected' },
};

export default handleActions(
  {
    // local actions
    [startCall]: (state, { payload: { type, to, from } }) => ({
      ...state,
      ...initialState,
      type,
      local: {
        ...initLocalState,
        isCalling: true,
        user: from,
      },
      remotes: [
        {
          ...initRemoteState,
          user: to,
          direction: 'outgoing',
        },
      ],
    }),

    [acceptCall]: (state, { payload: { uuid } }) => ({
      ...state,
      local: {
        ...state.local,
        isAccepted: true,
      },
      remotes: state.remotes.map(remote =>
        remote.uuid === uuid
          ? {
              ...remote,
              isAccepted: true,
            }
          : remote
      ),
    }),

    [rejectCall]: (state, { payload: { uuid } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isRejected: true,
      },
      remotes: state.remotes.map(remote => (remote.uuid === uuid ? { ...remote, isRejected: true } : remote)),
    }),

    [cancelCall]: (state, { payload: { uuid } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isCancelled: true,
      },
      remotes: state.remotes.map(remote => (remote.uuid === uuid ? { ...remote, isCancelled: true } : remote)),
    }),

    [endCall]: (state, { payload: { uuid } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isEnded: true,
      },
      remotes: state.remotes.map(remote =>
        remote.uuid === uuid ? { ...remote, isEnded: true, stream: null } : remote
      ),
    }),

    [setLocalStream]: (state, { payload: { stream } }) => ({
      ...state,
      local: { ...state.local, stream, streamVersion: state.local.streamVersion + 1 },
    }),

    [toggleMic]: (state, { payload: { isEnabled } }) => ({
      ...state,
      local: { ...state.local, isMicrophoneMuted: _isBoolean(isEnabled) ? !isEnabled : !state.local.isMicrophoneMuted },
    }),

    [toggleCamera]: (state, { payload: { isEnabled } }) => ({
      ...state,
      local: { ...state.local, isCameraEnabled: _isBoolean(isEnabled) ? isEnabled : !state.local.isCameraEnabled },
    }),

    [toggleScreenSharingSuccess]: (state, { payload: { isEnabled } }) => ({
      ...state,
      local: {
        ...state.local,
        isScreenSharingEnabled: _isBoolean(isEnabled) ? isEnabled : !state.local.isScreenSharingEnabled,
      },
    }),

    [changeCameraFacingMode]: (state, { mode }) => ({ ...state, cameraFacingMode: mode }),

    [toggleCameraFacingMode]: state => ({
      ...state,
      local: { ...state.local, cameraFacingMode: state.local.cameraFacingMode === 'user' ? 'environment' : 'user' },
    }),

    [resetCall]: state => ({ ...state, ...initialState }),

    // remote actions

    [resetRemoteCall]: (state, { payload: { from } }) => ({
      ...state,
      remotes: state.remotes.filter(remote => _get(remote, 'user.id') !== from),
    }),

    [newCustomerCall]: (
      state,
      { payload: { username, name, role, isAnonymousUser, customerVideoCall, uuid, type } }
    ) => ({
      ...state,
      type,
      local: initLocalState,
      remotes: [
        {
          ...initRemoteState,
          user: {
            id: username,
            name: name,
            role: role,
            isAnonymousUser: isAnonymousUser,
            customerVideoCall: customerVideoCall,
          },
          type: type,
          isCalling: true,
          direction: 'incoming',
          uuid,
        },
      ],
    }),

    [startCallByRemote]: (state, { payload: { from, type, uuid } }) => ({
      ...state,
      ...initialState,
      type,
      local: initLocalState,
      remotes: [
        {
          ...initRemoteState,
          user: from,
          type,
          isCalling: true,
          direction: 'incoming',
          uuid,
        },
      ],
    }),

    [acceptCallByRemote]: (state, { payload: { from } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id ? { ...remote, isAccepted: true } : remote
      ),
    }),

    [setRemoteStream]: (state, { payload: { from, stream } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isAccepted: true,
              stream,
              streamVersion: remote.streamVersion + 1,
            }
          : remote
      ),
    }),

    [rejectCallByRemote]: (state, { payload: { from } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isRejected: true,
        isCalling: false,
      },
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isRejected: true,
            }
          : remote
      ),
    }),

    [cancelCallByRemote]: (state, { payload: { from } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isCancelled: true,
        isCalling: false,
      },
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isCancelled: true,
            }
          : remote
      ),
    }),

    [endCallByRemote]: (state, { payload: { from } }) => ({
      ...state,
      local: {
        ...state.local,
        stream: null,
        isEnded: true,
        isCalling: false,
      },
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isEnded: true,
            }
          : remote
      ),
    }),

    [toggleRemoteMic]: (state, { payload: { from, isEnabled } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isMicrophoneMuted: !isEnabled,
            }
          : remote
      ),
    }),

    [toggleRemoteCamera]: (state, { payload: { from, isEnabled } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isCameraEnabled: isEnabled,
            }
          : remote
      ),
    }),

    [toggleRemoteScreenSharing]: (state, { payload: { from, isEnabled } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              isScreenSharingEnabled: isEnabled,
            }
          : remote
      ),
    }),

    [changeRemoteCameraFacingMode]: (state, { payload: { from, mode } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === from.id
          ? {
              ...remote,
              cameraFacingMode: mode,
            }
          : remote
      ),
    }),

    [updateCallId]: (state, { payload: { uuid, userId } }) => ({
      ...state,
      remotes: state.remotes.map(remote =>
        _get(remote, 'user.id') === userId
          ? {
              ...remote,
              uuid,
            }
          : remote
      ),
    }),

    [toggleChatVisibility]: (state, { payload: isChatVisible }) => ({
      ...state,
      isChatVisible: _isBoolean(isChatVisible) ? isChatVisible : !state.isChatVisible,
    }),

    [initialized]: state => ({
      ...state,
      isInit: true,
    }),

    [setStatus]: (state, { payload: { status } }) => ({ ...state, status }),

    [sendViewerSettings]: (state, { payload: { eventName, args } }) => {
      if (eventName !== 'send_viewer_settings') {
        return state;
      }
      return {
        ...state,
        remotes: state.remotes.map(remote =>
          _get(remote, 'user.id') === args.to.id
            ? {
                ...remote,
                user: { ...remote.user, customerVideoCall: args.customerVideoCall },
              }
            : remote
        ),
      };
    },
  },
  { ...initialState, isInit: false }
);
